<template>
  <div v-if="visible" class="mask">
    <section class="login-modal">
      <button class="site-modal-close" type="button" aria-label="关闭登录弹窗" @click="closeModal">×</button>
      <div class="login-art" aria-hidden="true">
        <div class="tech-grid"></div>
        <div class="tech-trace trace-a"><i></i><i></i><i></i></div>
        <div class="tech-trace trace-b"><i></i><i></i></div>

        <div class="art-brand-mark" aria-hidden="true">
          <span class="login-paper-mark login-paper-mark--art">
            <i
              class="login-paper-mark__sheet login-paper-mark__sheet--back"
            ></i>
            <i
              class="login-paper-mark__sheet login-paper-mark__sheet--front"
            ></i>
            <i class="login-paper-mark__fold"></i>
          </span>
        </div>

        <div class="interface-panel">
          <div class="interface-top">
            <span></span><span></span><span></span>
          </div>
          <div class="interface-body">
            <div class="interface-rail">
              <i class="active"></i><i></i><i></i><i></i>
            </div>
            <div class="interface-canvas">
              <i class="data-line line-primary"></i>
              <i class="data-line line-secondary"></i>
              <i class="data-line line-short"></i>
              <div class="signal-bars"><i></i><i></i><i></i><i></i><i></i></div>
              <div class="ai-chip">AI</div>
            </div>
          </div>
        </div>

        <div class="floating-sheet">
          <span class="sheet-fold"></span>
          <i></i><i></i><i></i><i></i>
        </div>
        <div class="floating-check">✓</div>
      </div>
      <div class="login-main">
        <div class="brand">
          <span class="login-paper-mark">
            <i
              class="login-paper-mark__sheet login-paper-mark__sheet--back"
            ></i>
            <i
              class="login-paper-mark__sheet login-paper-mark__sheet--front"
            ></i>
            <i class="login-paper-mark__fold"></i>
          </span>
          <strong>Paper 智创</strong>
        </div>
        <h2>微信扫码登录</h2>
        <p class="sub">扫码关注微信服务号后即可登录</p>
        <div class="login-badges">
          <span>仅支持微信扫码</span>
          <span>关注后自动登录</span>
        </div>
        <div class="qr-card">
          <div class="qr-shell" :class="{ warn: showAgreementCover }">
            <img
              v-if="qrCodeUrl"
              :src="qrCodeUrl"
              alt="微信登录二维码"
              @error="handleQrImageError"
            />
            <div v-else class="qr-placeholder">{{ qrPlaceholder }}</div>
            <div v-if="showAgreementCover" class="qr-cover">
              <span>请先阅读并同意</span>
              <span>用户服务协议和隐私政策</span>
            </div>
          </div>
          <button
            class="refresh"
            type="button"
            :disabled="loading"
            @click="createQrCode"
          >
            刷新
          </button>
          <span v-if="needAgree && !statusIsError" class="qr-error"
            >请先勾选协议</span
          >
          <span v-else :class="{ 'qr-error': statusIsError }">{{
            statusText
          }}</span>
        </div>
        <label class="agreement" :class="{ warn: needAgree }">
          <input v-model="agreed" type="checkbox" />
          <span>
            我已阅读并同意
            <a :href="termsHref" target="_blank" rel="noopener"
              >《用户服务协议》</a
            >
            和
            <a :href="privacyHref" target="_blank" rel="noopener"
              >《隐私政策》</a
            >
          </span>
        </label>
      </div>
    </section>
  </div>
</template>

<script>
import { getWxPcLoginQrCode, getWxPcLoginStatus } from "../api/wxPcLogin";

export default {
  name: "LoginModal",
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      agreed: true,
      needAgree: false,
      loginId: "",
      qrCodeUrl: "",
      loginStatus: "IDLE",
      statusText: "",
      pendingLogin: null,
      pollTimer: null,
      expireTimer: null,
      requestVersion: 0,
    };
  },
  computed: {
    loading() {
      return this.loginStatus === "LOADING";
    },
    statusIsError() {
      return ["ERROR", "EXPIRED"].includes(this.loginStatus);
    },
    showAgreementCover() {
      return !this.agreed && Boolean(this.qrCodeUrl) && !this.statusIsError;
    },
    qrPlaceholder() {
      if (this.loading) return "二维码生成中...";
      if (this.loginStatus === "EXPIRED") return "二维码已过期";
      return "二维码加载失败";
    },
    termsHref() {
      return this.$router.resolve({ name: "terms" }).href;
    },
    privacyHref() {
      return this.$router.resolve({ name: "privacy" }).href;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.agreed = true;
        this.needAgree = false;
        this.pendingLogin = null;
        this.createQrCode();
      } else {
        this.stopLoginSession();
      }
    },
    agreed(value) {
      if (value) {
        this.needAgree = false;
        if (this.pendingLogin) this.completeLogin();
      } else if (this.qrCodeUrl) {
        this.needAgree = true;
      }
    },
  },
  mounted() {
    if (this.visible) this.createQrCode();
  },
  beforeDestroy() {
    this.stopLoginSession();
  },
  methods: {
    async createQrCode() {
      this.stopTimers();
      const version = ++this.requestVersion;
      this.loginId = "";
      this.qrCodeUrl = "";
      this.pendingLogin = null;
      this.needAgree = false;
      this.loginStatus = "LOADING";
      this.statusText = "正在生成二维码...";

      try {
        const body = await getWxPcLoginQrCode();
        if (version !== this.requestVersion || !this.visible) return;

        const data = body.data || {};
        if (!data.loginId || !data.qrCodeUrl)
          throw new Error("二维码数据不完整");

        this.loginId = data.loginId;
        this.qrCodeUrl = data.qrCodeUrl;
        this.loginStatus = "WAITING";
        this.statusText = "请使用微信扫描二维码关注登录";
        this.needAgree = !this.agreed;

        const expiresIn = Number(data.expiresIn) || 300;
        this.expireTimer = window.setTimeout(() => {
          if (version !== this.requestVersion) return;
          this.stopTimers();
          this.loginStatus = "EXPIRED";
          this.statusText = "二维码已过期，请刷新后重试";
        }, expiresIn * 1000);
        this.schedulePoll(version);
      } catch (error) {
        if (version !== this.requestVersion) return;
        this.loginStatus = "ERROR";
        this.statusText = error.message || "二维码生成失败，请刷新后重试";
      }
    },
    schedulePoll(version) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = window.setTimeout(
        () => this.pollLoginStatus(version),
        2000,
      );
    },
    async pollLoginStatus(version) {
      if (version !== this.requestVersion || !this.visible || !this.loginId)
        return;

      try {
        const body = await getWxPcLoginStatus(this.loginId);
        if (version !== this.requestVersion || !this.visible) return;

        const data = body.data || {};
        if (data.status === "SUCCESS") {
          this.stopTimers();
          this.pendingLogin = { token: data.token, user: data.user || {} };
          if (this.agreed) {
            this.completeLogin();
          } else {
            this.loginStatus = "SCANNED";
            this.statusText = "扫码成功，请勾选协议后登录";
            this.needAgree = true;
          }
          return;
        }
        if (data.status === "EXPIRED") {
          this.stopTimers();
          this.loginStatus = "EXPIRED";
          this.statusText = "二维码已过期，请刷新后重试";
          return;
        }

        this.loginStatus = "WAITING";
        this.statusText = "请使用微信扫描二维码关注登录";
      } catch (error) {
        if (version !== this.requestVersion) return;
        this.statusText = "网络异常，正在重试...";
      }

      this.schedulePoll(version);
    },
    completeLogin() {
      if (!this.pendingLogin) return;
      const result = this.pendingLogin;
      this.pendingLogin = null;
      this.$emit("success", result);
    },
    handleQrImageError() {
      this.qrCodeUrl = "";
      this.loginStatus = "ERROR";
      this.statusText = "二维码图片加载失败，请刷新后重试";
      this.stopTimers();
    },
    closeModal() {
      this.stopLoginSession();
      this.$emit("close");
    },
    stopTimers() {
      window.clearTimeout(this.pollTimer);
      window.clearTimeout(this.expireTimer);
      this.pollTimer = null;
      this.expireTimer = null;
    },
    stopLoginSession() {
      this.requestVersion += 1;
      this.stopTimers();
      this.loginId = "";
      this.pendingLogin = null;
    },
  },
};
</script>

<style scoped>
.mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(16, 24, 39, 0.58);
  backdrop-filter: blur(5px);
}

.login-modal {
  position: relative;
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  width: min(840px, 100%);
  min-height: 560px;
  overflow: hidden;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 30px 90px rgba(16, 24, 39, 0.32);
}

.login-art {
  position: relative;
  min-width: 0;
  overflow: hidden;
  background: var(--brand-blue-soft);
}

.tech-grid {
  position: absolute;
  inset: 0;
  opacity: 0.45;
  background-image: linear-gradient(
      rgba(63, 130, 232, 0.12) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(63, 130, 232, 0.12) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: linear-gradient(
    to bottom,
    transparent 3%,
    #000 38%,
    #000 75%,
    transparent 100%
  );
}

.login-art::before {
  content: "";
  position: absolute;
  left: -54px;
  top: 104px;
  width: 270px;
  height: 52px;
  background: var(--brand-blue);
  transform: rotate(-28deg);
  opacity: 0.94;
}

.login-art::after {
  content: "";
  position: absolute;
  right: -80px;
  bottom: 56px;
  width: 230px;
  height: 64px;
  border: 18px solid rgba(122, 189, 230, 0.22);
  transform: rotate(-28deg);
}

.art-brand-mark {
  position: absolute;
  top: 42px;
  left: 36px;
  z-index: 2;
  width: 44px;
  height: 44px;
}

.login-paper-mark {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  animation: login-paper-mark-float 5s ease-in-out infinite;
}

.login-paper-mark__sheet {
  position: absolute;
  display: block;
  border-radius: 7px;
}

.login-paper-mark__sheet--back {
  top: 4px;
  right: 0;
  bottom: 0;
  left: 4px;
  background: var(--brand-teal);
  opacity: 0.72;
  transform: rotate(8deg);
}

.login-paper-mark__sheet--front {
  top: 0;
  right: 3px;
  bottom: 3px;
  left: 0;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: linear-gradient(
    145deg,
    var(--brand-blue-light) 0%,
    var(--brand-blue) 62%,
    #385f95 100%
  );
  box-shadow: 0 8px 16px rgba(63, 130, 232, 0.2);
  clip-path: polygon(0 0, 68% 0, 100% 32%, 100% 100%, 0 100%);
}

.login-paper-mark__fold {
  position: absolute;
  top: 1px;
  left: 18px;
  width: 8px;
  height: 8px;
  border-left: 1px solid rgba(63, 130, 232, 0.7);
  border-bottom: 1px solid rgba(63, 130, 232, 0.7);
  background: #b8d0e8;
}

.login-paper-mark--art {
  width: 44px;
  height: 44px;
}

.login-paper-mark--art .login-paper-mark__sheet--back {
  top: 6px;
  left: 6px;
}

.login-paper-mark--art .login-paper-mark__sheet--front {
  right: 4px;
  bottom: 4px;
}

.login-paper-mark--art .login-paper-mark__fold {
  left: 25px;
  width: 11px;
  height: 11px;
}

.tech-trace {
  position: absolute;
  z-index: 1;
  height: 1px;
  background: rgba(63, 130, 232, 0.35);
}

.tech-trace::before {
  content: "";
  position: absolute;
  top: -4px;
  left: 0;
  width: 9px;
  height: 9px;
  border: 2px solid var(--brand-blue);
  border-radius: 50%;
  background: var(--brand-blue-soft);
}

.tech-trace i {
  position: absolute;
  top: -2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--brand-sky);
}

.tech-trace i:nth-child(1) {
  left: 28%;
}
.tech-trace i:nth-child(2) {
  left: 58%;
}
.tech-trace i:nth-child(3) {
  left: 84%;
}

.trace-a {
  top: 104px;
  right: 26px;
  width: 190px;
}

.trace-b {
  left: 34px;
  bottom: 66px;
  width: 150px;
}

.interface-panel {
  position: absolute;
  top: 154px;
  left: 50%;
  z-index: 2;
  width: 286px;
  height: 244px;
  overflow: hidden;
  border: 1px solid #cbdcf5;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 28px 58px rgba(63, 130, 232, 0.18);
  transform: translateX(-50%) rotate(-2deg);
}

.interface-top {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 13px;
  border-bottom: 1px solid #dbe7f7;
  background: #f8fbff;
}

.interface-top span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b8c7dc;
}

.interface-top span:first-child {
  background: var(--brand-blue);
}

.interface-body {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  height: calc(100% - 34px);
}

.interface-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding-top: 20px;
  border-right: 1px solid #e2ebf7;
  background: #f5f9ff;
}

.interface-rail i {
  width: 12px;
  height: 12px;
  border: 2px solid #b3c4db;
  border-radius: 3px;
}

.interface-rail i.active {
  border-color: var(--brand-blue);
  background: var(--brand-blue);
}

.interface-canvas {
  position: relative;
  padding: 28px 24px;
}

.data-line {
  display: block;
  height: 7px;
  margin-bottom: 10px;
  border-radius: 3px;
  background: #dce8f7;
}

.line-primary {
  width: 58%;
  background: var(--brand-blue);
}

.line-secondary {
  width: 84%;
}
.line-short {
  width: 44%;
}

.signal-bars {
  position: absolute;
  left: 25px;
  bottom: 26px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 74px;
}

.signal-bars i {
  width: 12px;
  border-radius: 3px 3px 0 0;
  background: #a8c7f7;
}

.signal-bars i:nth-child(1) {
  height: 28px;
}
.signal-bars i:nth-child(2) {
  height: 46px;
}
.signal-bars i:nth-child(3) {
  height: 37px;
}
.signal-bars i:nth-child(4) {
  height: 65px;
  background: var(--brand-blue);
}
.signal-bars i:nth-child(5) {
  height: 54px;
  background: #75b0c4;
}

.ai-chip {
  position: absolute;
  right: 24px;
  bottom: 36px;
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border: 8px solid #dceaff;
  border-right-color: var(--brand-blue);
  border-radius: 50%;
  color: #fff;
  background: #365f99;
  font-size: 12px;
  font-weight: 900;
}

.floating-sheet {
  position: absolute;
  z-index: 3;
  left: 38px;
  bottom: 64px;
  width: 105px;
  height: 132px;
  padding: 42px 15px 14px;
  border: 1px solid #c8daf2;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 38px rgba(63, 130, 232, 0.2);
  transform: rotate(7deg);
}

.sheet-fold {
  position: absolute;
  top: 0;
  right: 0;
  width: 27px;
  height: 27px;
  border-left: 1px solid #c8daf2;
  border-bottom: 1px solid #c8daf2;
  background: var(--brand-blue-soft);
}

.floating-sheet > i {
  display: block;
  height: 5px;
  margin-bottom: 8px;
  border-radius: 2px;
  background: #d7e4f5;
}

.floating-sheet > i:first-of-type {
  width: 58%;
  background: #75b0c4;
}

.floating-sheet > i:last-child {
  width: 70%;
}

.floating-check {
  position: absolute;
  right: 34px;
  bottom: 84px;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border: 7px solid #fff;
  border-radius: 50%;
  color: #fff;
  background: var(--brand-blue);
  box-shadow: 0 16px 32px rgba(63, 130, 232, 0.28);
  font-size: 22px;
  font-weight: 900;
}

.login-main {
  padding: 58px 34px 30px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand > .login-paper-mark {
  flex: 0 0 auto;
}

.brand strong {
  color: #172033;
  font-size: 19px;
}

h2 {
  margin: 18px 0 0;
  color: #101827;
  font-size: 28px;
}

.sub {
  margin: 10px 0 24px;
  color: #617088;
}

.login-badges {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.login-badges span {
  padding: 5px 11px;
  border-radius: 999px;
  color: var(--brand-blue);
  background: #edf4ff;
  font-size: 12px;
  font-weight: 700;
}

.qr-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 0 14px;
}

.qr-shell {
  position: relative;
  display: grid;
  place-items: center;
  width: 220px;
  height: 220px;
  overflow: hidden;
  border: 1px solid #d7e4f5;
  border-radius: 16px;
  background: #fff;
}

.qr-shell img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-placeholder {
  padding: 20px;
  color: #8190a7;
  text-align: center;
  font-size: 14px;
}

.qr-cover {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 20px;
  color: #fff;
  background: rgba(15, 23, 42, 0.66);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
}

.qr-cover span {
  display: block;
}

.refresh {
  min-height: 34px;
  padding: 0 15px;
  border: 1px solid #d8e3f0;
  border-radius: 999px;
  color: #486582;
  background: #f5f8fc;
}

.refresh:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.qr-card > span {
  color: #8190a7;
  font-size: 13px;
}

.qr-error {
  color: #ef4444 !important;
  font-weight: 800;
}

.agreement {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  color: #66758d;
  font-size: 13px;
  line-height: 1.6;
}

.agreement.warn {
  color: #ef4444;
}

.agreement input {
  margin-top: 3px;
}

.agreement a {
  color: var(--brand-blue);
  font-weight: 700;
}

@keyframes login-paper-mark-float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }

  50% {
    transform: translateY(-3px) rotate(-2deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-paper-mark {
    animation: none;
  }
}

@media (max-width: 760px) {
  .login-modal {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .login-art {
    display: none;
  }

  .login-main {
    padding: 62px 22px 24px;
  }
}
</style>
